import React from 'react';
import { AlertTriangle, CalendarClock, KeyRound, Loader, MapPin } from 'lucide-react';
import Sheet from '../common/Sheet';
import type { Authorization } from '../../dtos/patientmr/authorizations.dto';
import type { NursingServiceType } from '../../utils/evvRules';
import { paOptionLabel } from '../../utils/visitStart';
import { shiftWindow } from '../scheduling/scheduleUtils';

export interface StartVisitShiftInfo {
  id: string;
  scheduleRef: string | null;
  startTime: string;
  endTime: string;
  overnight: boolean;
  visitType: string;
}

export interface StartVisitSheetProps {
  open: boolean;
  onClose: () => void;
  patientName: string;
  /** The shift this visit works (from My Day or matched today) */
  shift?: StartVisitShiftInfo | null;
  /** PENDING_ADMISSION: only Initial / Evaluation (VIS-03) */
  pendingAdmission: boolean;
  visitType: 'Initial' | 'Evaluation' | '';
  onVisitTypeChange: (v: 'Initial' | 'Evaluation') => void;
  /** PA-14: eligible PAs only */
  pas: Authorization[];
  loadingPas: boolean;
  selectedPaId: string | null;
  onSelectPa: (id: string | null) => void;
  /** RN/LVN choose PDN shift vs skilled nursing visit (R-177) */
  isNursing: boolean;
  serviceType: NursingServiceType;
  onServiceTypeChange: (v: NursingServiceType) => void;
  /** HHAX clock-in prompt (VIS-02) */
  evvPrompt: boolean;
  evvRequired: boolean;
  clockIn: 'ok' | 'missed_prompt' | '';
  onClockIn: (v: 'ok' | 'missed_prompt') => void;
  location: { status: 'locating' | 'ok' | 'unavailable' };
  /**
   * Phase 18 M13 / F21: offer "Supervisory" (an RN on a patient with LVN or HHA staff on the
   * care team or schedule). A supervisory visit uses no authorization and is never billed.
   */
  supervisoryAvailable?: boolean;
  supervisory?: boolean;
  onSupervisoryChange?: (v: boolean) => void;
  error: string | null;
  busy: boolean;
  onConfirm: () => void;
}

const bigChoice = (active: boolean) =>
  `flex min-h-[48px] flex-1 items-center justify-center rounded-lg border-2 px-3 text-base font-semibold ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'}`;

/**
 * Start a visit (Phase 9B, plan §6.2). One sheet, preselected from the shift: PA on one line,
 * the HHAX question as two large buttons, location captured in the background. A missing PA or
 * location never blocks the start (VIS-04/06).
 */
const StartVisitSheet: React.FC<StartVisitSheetProps> = (p) => {
  const canConfirm = !p.busy && (!p.evvPrompt || Boolean(p.clockIn)) && (!p.pendingAdmission || Boolean(p.visitType));
  return (
    <Sheet
      open={p.open}
      onClose={() => !p.busy && p.onClose()}
      title="Start visit"
      subtitle={p.patientName}
      footer={(
        <div className="flex gap-2">
          <button type="button" onClick={p.onClose} disabled={p.busy} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 text-base font-medium text-gray-700 md:flex-none md:px-5">Cancel</button>
          <button
            type="button"
            onClick={p.onConfirm}
            disabled={!canConfirm}
            className="flex min-h-[48px] flex-[2] items-center justify-center gap-2 rounded-lg bg-emerald-700 text-base font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 md:flex-none md:px-6"
          >
            {p.busy ? <><Loader size={18} className="animate-spin" />Starting…</> : 'Start visit'}
          </button>
        </div>
      )}
    >
      <div className="space-y-5">
        {p.shift ? (
          <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <CalendarClock size={18} className="mt-0.5 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">Scheduled {shiftWindow(p.shift)} · {p.shift.visitType}</div>
              {p.shift.scheduleRef && <div className="font-mono text-xs text-gray-500">{p.shift.scheduleRef}</div>}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No shift found for now; this visit will be recorded as unscheduled.</p>
        )}

        {p.pendingAdmission && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700">Visit type (patient not admitted yet)</legend>
            <div className="flex gap-2">
              {(['Initial', 'Evaluation'] as const).map((v) => (
                <button key={v} type="button" className={bigChoice(p.visitType === v)} onClick={() => p.onVisitTypeChange(v)} aria-pressed={p.visitType === v}>{v}</button>
              ))}
            </div>
          </fieldset>
        )}

        {p.supervisoryAvailable && !p.pendingAdmission && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700">Visit type</legend>
            <div className="flex gap-2">
              <button type="button" className={bigChoice(!p.supervisory)} onClick={() => p.onSupervisoryChange?.(false)} aria-pressed={!p.supervisory}>Care visit</button>
              <button type="button" className={bigChoice(Boolean(p.supervisory))} onClick={() => p.onSupervisoryChange?.(true)} aria-pressed={Boolean(p.supervisory)}>Supervisory</button>
            </div>
            <p className="mt-2 text-xs text-gray-500" data-testid="supervisory-note">Supervisory visits are not billed.{p.supervisory ? ' Chart a supervisory note before you submit.' : ''}</p>
          </fieldset>
        )}

        {p.supervisory && p.supervisoryAvailable ? (
          <p className="flex items-center gap-2 text-sm text-gray-600"><KeyRound size={16} />No authorization is used for a supervisory visit.</p>
        ) : (
        <div>
          <label htmlFor="start-pa" className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-700"><KeyRound size={16} />Prior authorization</label>
          {p.loadingPas ? (
            <p className="text-sm text-gray-500">Loading authorizations…</p>
          ) : p.pas.length ? (
            <select
              id="start-pa"
              value={p.selectedPaId || ''}
              onChange={(e) => p.onSelectPa(e.target.value || null)}
              className="min-h-[48px] w-full rounded-lg border border-gray-300 bg-white px-3 text-base"
            >
              <option value="">No authorization (visit unlinked)</option>
              {p.pas.map((pa) => <option key={pa.id} value={pa.id}>{paOptionLabel(pa as never)}</option>)}
            </select>
          ) : (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              No approved authorization covers today. The visit starts unlinked; the office links it later.
            </div>
          )}
        </div>
        )}

        {p.isNursing && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700">This is a</legend>
            <div className="flex gap-2">
              {([['PDN', 'PDN shift'], ['SKILLED_NURSING_VISIT', 'Skilled nursing visit']] as const).map(([v, label]) => (
                <button key={v} type="button" className={bigChoice(p.serviceType === v)} onClick={() => p.onServiceTypeChange(v)} aria-pressed={p.serviceType === v}>{label}</button>
              ))}
            </div>
          </fieldset>
        )}

        {p.evvPrompt && (
          <fieldset>
            <legend className="mb-1 text-sm font-medium text-gray-700">Have you clocked in on HHAeXchange?</legend>
            <p className="mb-2 text-xs text-gray-500">
              {p.evvRequired ? 'This Medicaid visit requires EVV.' : 'Your agency records EVV for PDN visits.'} &quot;Not yet&quot; never blocks the visit.
            </p>
            <div className="flex gap-2">
              <button type="button" className={bigChoice(p.clockIn === 'ok')} onClick={() => p.onClockIn('ok')} aria-pressed={p.clockIn === 'ok'}>Yes, clocked in</button>
              <button type="button" className={bigChoice(p.clockIn === 'missed_prompt')} onClick={() => p.onClockIn('missed_prompt')} aria-pressed={p.clockIn === 'missed_prompt'}>Not yet</button>
            </div>
          </fieldset>
        )}

        <p className="flex items-center gap-2 text-xs text-gray-500">
          <MapPin size={14} />
          {p.location.status === 'locating' ? 'Getting your location…' : p.location.status === 'ok' ? 'Location captured' : 'Location unavailable — the visit is flagged, not blocked'}
        </p>

        {p.error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{p.error}</p>}
      </div>
    </Sheet>
  );
};

export default StartVisitSheet;
